import {
  Component,
  EventEmitter,
  OnInit,
  OnDestroy,
  Output,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  DestroyRef
} from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ConfirmationModalConfig } from '../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { EcThresholdDeleteService } from './ec-threshold-delete.service';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  normalizeFilterParams,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams
} from '../../shared/workspace-element-context';

/**
 * EC Threshold Management batch delete confirmation.
 *
 * Replaces the legacy `delete-modal.html` (a plain "Confirm Delete" Yes/No box)
 * using the shared `gtw-confirmation-modal`. Launched from the grid via
 * `deleteThresholdMgmtData` -> `gtw-ec-threshold-delete-ce`. On confirm it batch
 * deletes the selected grid rows (action_code=p8r1d0).
 */
@Component({
  selector: 'gtw-ec-threshold-delete-ce',
  templateUrl: './ec-threshold-delete.component.html',
  styleUrls: ['./ec-threshold-delete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class EcThresholdDeleteComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();
  private isInitialized = false;
  crudLoading = false;
  showModal = false;

  globalParams: any = {};
  filterParams: any = {};
  gridData: any[] = [];
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};

  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm Delete',
    message: 'Are you sure you want to delete All Data for that Year/Scenario?',
    iconClass: 'fa fa-trash-o',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private ecThresholdDeleteService: EcThresholdDeleteService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => { this.applyWorkspaceContext(); this.tryInit(); }));
    this.tryInit();
  }

  ngOnDestroy(): void { this.subs.unsubscribe(); }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.filterParams = normalizeFilterParams(activeScreen);

    // Selected grid rows are bridged into activeScreen.data.gridData by CeHostDialog;
    // migrated components read context from the workspace service, not @Input attributes.
    const bridged = gridPayloadFromActiveScreen(activeScreen);
    this.gridData = Array.isArray(bridged) ? bridged : [];

    this.cdr.detectChanges();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    const hasConfig = this.GENERAL_CONFIG?.base_url;
    const hasFilterParams = this.filterParams && Object.keys(this.filterParams).length > 0;
    if (!hasConfig || !hasFilterParams) return;
    this.isInitialized = true;
    this.showModal = true;
    this.cdr.detectChanges();
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  confirmDelete(): void {
    const rows: any[] = [];
    if (Array.isArray(this.gridData)) {
      this.gridData.forEach((el: any) => {
        rows.push(el?.data ?? el);
      });
    }

    this.crudLoading = true;
    this.modalConfig = { ...this.modalConfig, loading: true };
    this.cdr.detectChanges();

    this.ecThresholdDeleteService.deleteThresholdData(
      this.GENERAL_CONFIG.base_url,
      rows,
      this.globalParams,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: any) => {
        this.crudLoading = false;
        this.modalConfig = { ...this.modalConfig, loading: false };
        const hasError = response?.errorMessage && response.errorMessage !== 'null';
        const isSuccess = response?.callSuccess === '1' && !hasError;
        if (isSuccess) {
          this.gtwSnackbarService.openSnackBar('Threshold Management deleted successfully', 'gtw-snackbar--green');
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.crudLoading = false;
        this.modalConfig = { ...this.modalConfig, loading: false };
        this.gtwSnackbarService.openSnackBar('Error occurred while deleting. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    });
  }
}
